import { useEffect, useRef } from 'react';
import { Button, Input } from '../ui';
import { CountrySelect } from './CountrySelect';
import { usePhoneVerification, type CodeSentHandler } from '../../../lib/use-phone-verification';
import { markSymphonyWelcomeSeen } from '../../SymphonyWelcomeModal';
import { useTranslation } from '../../../lib/i18n';
import styles from './PhoneScreen.module.css';

// The number fields. The verification dialog is the only surface that asks for a number — there is no
// full-screen version any more, and `/onboarding/phone` forwards to `/welcome` (see onboarding-gate's
// RETIRED_ONBOARDING). `onCodeSent` is how the dialog moves to its own code step.
export function PhoneStepBody({ onCodeSent }: { onCodeSent?: CodeSentHandler } = {}) {
  const { t } = useTranslation();
  const phone = usePhoneVerification({ onCodeSent });
  const numberRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    markSymphonyWelcomeSeen();
    // React's `autoFocus` did not survive this screen's mount, and the number field is
    // the only thing to do here — so focus it explicitly.
    numberRef.current?.focus();
  }, []);

  // The AppsFlyer onboarding-start event no longer fires here: it is now latched once per
  // browser at the user's first chat send while onboarding is unfinished (see lib/use-chat.ts
  // + lib/appsflyer.ts), not on the phone/verify screen.
  function startAndSend(send: () => Promise<void>) {
    void send();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        startAndSend(phone.sendSms);
      }}
      noValidate
    >
      <div className={styles.row}>
        <CountrySelect
          countries={phone.countries}
          value={phone.iso}
          onChange={(next) => {
            phone.logCountryPick(next);
            phone.setIso(next);
          }}
          onOpen={phone.logCountryListOpen}
          disabled={phone.submitting}
        />
        <Input
          ref={numberRef}
          className={styles.number}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={t('onboarding.phone.input.label')}
          aria-label={t('onboarding.phone.input.label')}
          value={phone.phone}
          onChange={(e) => phone.setPhone(e.target.value)}
          onBlur={phone.onBlur}
          status={phone.showInvalid ? 'error' : 'default'}
          statusMessage={phone.showInvalid ? t('onboarding.phone.error.invalidNumber') : undefined}
        />
      </div>

      {phone.error && (
        <p className={styles.error} role="alert">
          {phone.error}
        </p>
      )}

      <div className={styles.actions}>
        <Button type="submit" size="large" fullWidth disabled={!phone.isValid} loading={phone.submitting}>
          {t('onboarding.phone.symphony.cta.sms')}
        </Button>
        <Button
          type="button"
          size="large"
          fullWidth
          variant="outlined"
          disabled={!phone.isValid || phone.submitting}
          onClick={() => startAndSend(phone.sendWhatsApp)}
        >
          {t('onboarding.phone.symphony.cta.whatsapp')}
        </Button>
      </div>
    </form>
  );
}
